/**
 * AskPage — The family memory search interface (Phases 5, 6, 7).
 *
 * Three framing states based on meta.confidence:
 *   confident: "Here's what I found about X"
 *   marginal:  "I couldn't find much — these came closest" (visually distinct)
 *   empty:     dedicated empty state with recovery chips
 *
 * Hedging is a REQUIREMENT, not polish. ~30% of unanswerable questions will
 * return results because the retrieval signal cannot separate them.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Mic, MicOff, Loader2, BookOpen, Feather, Clock, Play, ChevronRight } from 'lucide-react';
import { askQuestion, getSuggestionChips } from '../services/retrievalAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

// Loading placeholder chips — replaced by family-specific chips on mount
const PLACEHOLDER_CHIPS = [
  "What did Grandma say about her childhood?",
  "Family recipes and cooking traditions",
  "Stories about coming to America",
  "Advice the elders have shared",
  "What happened at the wedding?",
];

// --- Voice (Phase 6) ---
/**
 * Get a SpeechRecognition instance.
 * @param {string} [userLang] - User's language preference from profile (Person.language).
 *   Falls back to navigator.language. NOT hardcoded to en-US — the core use case
 *   is an immigrant grandparent speaking Tagalog on an English browser.
 *
 *   PRODUCT GAP: Person.language exists but there is no UI to set it. Until that
 *   exists, navigator.language is the only signal available.
 */
const getSpeechRecognition = (userLang) => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const recognition = new SR();
  if ('processLocally' in recognition) {
    recognition.processLocally = true;
  }
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.lang = userLang || navigator.language || 'en';
  return recognition;
};

const isCloudRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return false;
  const r = new SR();
  return !('processLocally' in r && r.processLocally);
};

export default function AskPage() {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [disclosureAcked, setDisclosureAcked] = useState(
    () => localStorage.getItem('voice_disclosure_acked') === 'true'
  );
  const [chips, setChips] = useState(PLACEHOLDER_CHIPS);
  const [chipsLoaded, setChipsLoaded] = useState(false);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch family-specific chips on mount (Phase 7, Task 14)
  useEffect(() => {
    getSuggestionChips().then(serverChips => {
      if (serverChips && serverChips.length > 0) {
        setChips(serverChips);
      }
      setChipsLoaded(true);
    }).catch(() => setChipsLoaded(true));
  }, []);

  // --- Search ---
  const handleAsk = useCallback(async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const data = await askQuestion(text);
      setResponse(data);
    } catch (err) {
      if (err.response?.status === 429) {
        setResponse({
          status: 'throttled',
          message: err.response.data?.message || "Please wait a moment before asking another question.",
          retry_after: err.response.data?.retry_after || 60,
        });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [question, loading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAsk();
  };

  const handleChipClick = (chipText) => {
    setQuestion(chipText);
    handleAsk(chipText);
  };

  const handleFollowUp = (followUp) => {
    setQuestion(followUp);
    handleAsk(followUp);
  };

  // --- Voice (Phase 6) ---
  const startListening = () => {
    if (!disclosureAcked && isCloudRecognition()) {
      setShowDisclosure(true);
      return;
    }
    const recognition = getSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceTranscript('');
    setListening(true);

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(r => r[0].transcript)
        .join('');
      setVoiceTranscript(transcript);
      if (event.results[0].isFinal) {
        setQuestion(transcript);
        setListening(false);
        // Show transcription for review, don't auto-search
      }
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognition.start();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const ackDisclosure = () => {
    localStorage.setItem('voice_disclosure_acked', 'true');
    setDisclosureAcked(true);
    setShowDisclosure(false);
    startListening();
  };

  // --- Confidence level ---
  const confidence = response?.meta?.confidence || (response?.status === 'empty' ? 'empty' : 'confident');
  const isThrottled = response?.status === 'throttled';
  const isEmpty = response?.status === 'empty' || confidence === 'empty';
  const isMarginal = confidence === 'marginal';
  const isDegraded = response?.meta?.composed === false && response?.items?.length > 0;

  // --- Render ---
  const hasVoice = !!getSpeechRecognition();

  return (
    <div style={{
      maxWidth: 800, margin: '0 auto', padding: '24px 16px',
      fontFamily: FONT_B, color: '#2C2F26',
      minHeight: '100vh', background: '#FAFAF5',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <BookOpen size={32} color="#556B2F" style={{ marginBottom: 8 }} />
        <h1 style={{ fontFamily: FONT_H, fontSize: 28, fontWeight: 700, color: '#2C2F26', margin: 0 }}>
          Ask about your family
        </h1>
        <p style={{ color: '#7C846A', fontSize: 16, marginTop: 6 }}>
          Search your family's stories, memories, and wisdom
        </p>
      </div>

      {/* Search input */}
      <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#fff', borderRadius: 16, padding: '10px 16px',
          border: '1.5px solid #D4D1C4', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <Search size={20} color="#9AA088" />
          <input
            ref={inputRef}
            type="text"
            value={listening ? voiceTranscript : question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What would you like to know?"
            disabled={loading || listening}
            aria-label="Ask a question about your family"
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 18,
              fontFamily: FONT_B, color: '#2C2F26', background: 'transparent',
              minHeight: 44,
            }}
          />
          {hasVoice && (
            <button
              type="button"
              onClick={listening ? stopListening : startListening}
              disabled={loading}
              aria-label={listening ? 'Stop listening' : 'Search by voice'}
              style={{
                background: listening ? '#E65100' : '#F5F3ED',
                border: 'none', borderRadius: 12, padding: 10,
                cursor: 'pointer', minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {listening
                ? <MicOff size={20} color="#fff" />
                : <Mic size={20} color="#556B2F" />
              }
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !question.trim()}
            aria-label="Search"
            style={{
              background: question.trim() && !loading ? '#556B2F' : '#D4D1C4',
              color: '#fff', border: 'none', borderRadius: 12,
              padding: '10px 20px', fontSize: 16, fontWeight: 600,
              cursor: question.trim() && !loading ? 'pointer' : 'not-allowed',
              minHeight: 44, fontFamily: FONT_B,
            }}
          >
            {loading ? <Loader2 size={18} className="spin" /> : 'Ask'}
          </button>
        </div>
      </form>

      {/* Voice disclosure (Phase 6, Task 9) */}
      {showDisclosure && (
        <div style={{
          background: '#FFF8E7', border: '1px solid #E8D5A0', borderRadius: 12,
          padding: 16, marginBottom: 16, fontSize: 15,
        }}>
          <p style={{ margin: '0 0 12px', fontWeight: 600 }}>Voice input</p>
          {/* PLACEHOLDER — do not ship until A3 closes and legal copy is supplied */}
          <p style={{ margin: '0 0 12px', color: '#5A5A4A' }}>
            [A3 PLACEHOLDER] Voice input is processed by your browser's speech service.
            Your spoken words may be sent to a third party for recognition.
          </p>
          <button
            onClick={ackDisclosure}
            style={{
              background: '#556B2F', color: '#fff', border: 'none',
              borderRadius: 10, padding: '10px 24px', fontSize: 15,
              fontWeight: 600, cursor: 'pointer', minHeight: 44,
            }}
          >
            I understand
          </button>
        </div>
      )}

      {/* Listening state */}
      {listening && (
        <div style={{
          textAlign: 'center', padding: 24, background: '#FFF5F0',
          borderRadius: 16, marginBottom: 16, border: '1.5px solid #E65100',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', background: '#E65100',
            margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Mic size={24} color="#fff" />
          </div>
          <p style={{ fontSize: 18, fontWeight: 600, color: '#E65100', margin: '0 0 8px' }}>
            Listening...
          </p>
          {voiceTranscript && (
            <p style={{ fontSize: 16, color: '#2C2F26', fontStyle: 'italic', margin: 0 }}>
              "{voiceTranscript}"
            </p>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={32} color="#556B2F" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#7C846A', marginTop: 12, fontSize: 16 }}>
            Searching your family's stories...
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: '#FFF0F0', borderRadius: 12, padding: 16,
          marginBottom: 16, color: '#8B0000', fontSize: 15,
        }}>
          {error}
        </div>
      )}

      {/* Throttled */}
      {isThrottled && (
        <div style={{
          background: '#FFF8E7', borderRadius: 16, padding: 24,
          textAlign: 'center', border: '1px solid #E8D5A0',
        }}>
          <Clock size={28} color="#B8860B" style={{ marginBottom: 8 }} />
          <p style={{ fontSize: 17, color: '#5A4A1A', margin: '0 0 8px', fontWeight: 500 }}>
            {response.message}
          </p>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && !loading && !isThrottled && response && (
        <div style={{
          textAlign: 'center', padding: 40, background: '#fff',
          borderRadius: 16, border: '1px solid #E4E1D2',
        }}>
          <Feather size={36} color="#9AA088" style={{ marginBottom: 12 }} />
          <p style={{
            fontFamily: FONT_H, fontSize: 20, fontWeight: 600,
            color: '#556B2F', margin: '0 0 8px',
          }}>
            I couldn't find anything about that in your family's stories yet.
          </p>
          <p style={{ color: '#7C846A', fontSize: 15, margin: '0 0 20px' }}>
            Try asking about a specific person, memory, or time in your family's life.
          </p>
          {/* Recovery chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {chips.slice(0, 4).map((chip, i) => (
              <button
                key={i}
                onClick={() => handleChipClick(chip)}
                style={{
                  background: '#F5F3ED', border: '1px solid #D4D1C4',
                  borderRadius: 20, padding: '8px 16px', fontSize: 14,
                  color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B,
                  minHeight: 44,
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {response?.items?.length > 0 && !isThrottled && (
        <div>
          {/* Framing text — hedged by confidence */}
          <div style={{
            padding: '16px 20px', borderRadius: 14, marginBottom: 16,
            background: isMarginal ? '#FFF8E7' : '#F0F5E8',
            border: isMarginal ? '1px solid #E8D5A0' : '1px solid #C8D5B0',
          }}>
            <p style={{
              fontSize: 17, color: isMarginal ? '#6B5A1A' : '#3A4A20',
              margin: 0, fontFamily: FONT_H, fontWeight: 500, lineHeight: 1.5,
            }}>
              {isMarginal && !response.framing_text
                ? "I couldn't find much about that — these came closest."
                : response.framing_text || "Here are the most relevant items from your family's archive."
              }
            </p>
            {isMarginal && (
              <p style={{ fontSize: 13, color: '#8B7A3A', margin: '6px 0 0', fontStyle: 'italic' }}>
                These results may not be exactly what you're looking for.
              </p>
            )}
          </div>

          {/* Result cards */}
          {response.items.map((item, i) => (
            <div
              key={item.item_id}
              role="link"
              tabIndex={0}
              style={{
                background: '#fff', borderRadius: 14, padding: 16,
                marginBottom: 12, border: '1px solid #E4E1D2',
                cursor: 'pointer', transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{
                  fontSize: 12, color: '#7C846A', textTransform: 'uppercase',
                  letterSpacing: 0.5, fontWeight: 600,
                }}>
                  {item.content_type === 'transcript_chunk' ? 'Recording' :
                   item.content_type === 'story' ? 'Story' :
                   item.content_type === 'wisdom' ? 'Wisdom' : item.content_type}
                </span>
                {item.content_date && (
                  <span style={{ fontSize: 12, color: '#9AA088' }}>{item.content_date}</span>
                )}
              </div>
              <p style={{
                fontSize: 16, color: '#2C2F26', margin: '0 0 8px',
                lineHeight: 1.55, fontFamily: FONT_B,
              }}>
                {item.text_preview}
              </p>
              {item.reason && (
                <p style={{ fontSize: 13, color: '#556B2F', margin: '0 0 4px', fontStyle: 'italic' }}>
                  {item.reason}
                </p>
              )}
              {/* Deep link to video playback at start_ms */}
              {item.start_ms != null && item.story_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const seconds = Math.floor(item.start_ms / 1000);
                    window.location.href = `/stories/${item.story_id}?t=${seconds}`;
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: '#F5F3ED', border: '1px solid #D4D1C4',
                    borderRadius: 8, padding: '6px 12px', fontSize: 13,
                    color: '#556B2F', cursor: 'pointer', marginTop: 4,
                    minHeight: 36,
                  }}
                >
                  <Play size={14} /> Listen from {Math.floor(item.start_ms / 60000)}:{String(Math.floor((item.start_ms % 60000) / 1000)).padStart(2, '0')}
                </button>
              )}
            </div>
          ))}

          {/* Follow-up chips */}
          {response.follow_up_questions?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 14, color: '#7C846A', marginBottom: 8 }}>
                You might also want to ask:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {response.follow_up_questions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleFollowUp(q)}
                    style={{
                      background: '#F0F5E8', border: '1px solid #C8D5B0',
                      borderRadius: 20, padding: '8px 16px', fontSize: 14,
                      color: '#3A4A20', cursor: 'pointer', fontFamily: FONT_B,
                      display: 'flex', alignItems: 'center', gap: 4, minHeight: 44,
                    }}
                  >
                    {q} <ChevronRight size={14} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial chips (no response yet) */}
      {!response && !loading && (
        <div>
          <p style={{ fontSize: 15, color: '#7C846A', marginBottom: 12, textAlign: 'center' }}>
            Try asking about...
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {chips.map((chip, i) => (
              <button
                key={i}
                onClick={() => handleChipClick(chip)}
                style={{
                  background: '#fff', border: '1px solid #D4D1C4',
                  borderRadius: 20, padding: '10px 18px', fontSize: 15,
                  color: '#556B2F', cursor: 'pointer', fontFamily: FONT_B,
                  minHeight: 44,
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
